import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Divider,
  Grid,
} from '@mui/material';
import Header from '../components/Admin/header/Header';
import Sidebar from '../components/Admin/Sidebar/Sidebar';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';

const Reports = () => {
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("name");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const REPORT_ITEMS = [
    {
      id: '1',
      label: 'Cash Collection',
      path: '/reports/cash-collection',
    },
    {
      id: '2',
      label: 'Breakup/Summary of Bill',
      path: '/reports/summary-bill',
    },
    {
      id: '3',
      label: 'Patient Wise Item Wise Expense Report',
      path: '/reports/expense-report',
    },
    {
      id: '4',
      label: 'IPD Patient Expense Detail Statement',
      path: '/reports/expense-detail-statement',
    },
    {
      id: '5',
      label: 'Patient Advance Payment Report',
      path: '/reports/advance-payment',
    },
    {
      id: '6',
      label: 'Month Wise Package Count Statement (IPD)',
      path: '/reports/package-count-statement',
    },
    {
      id: '7',
      label: 'IPD Discharge Patient Report',
      path: '/reports/ipd-discharge-patient',
    },
    {
      id: '8',
      label: 'IPD Patient Summary',
      path: '/reports/ipd-patient-summary',
    },
    {
      id: '9',
      label: 'Patient Indent Ledger Item Wise',
      path: '/reports/patient-indent',
    },
    {
      id: '10',
      label: 'Patient Indent Return Ledger Item Wise',
      path: '/reports/patient-indent-return',
    },
    {
      id: '11',
      label: 'Patient Ward Issue',
      path: '/reports/patient-ward-issue',
    },
    {
      id: '12',
      label: 'Cancel Payment Report',
      path: '/reports/cancel-payment',
    },
    {
      id: '13',
      label: 'Collection Report',
      path: '/reports/collection',
    },
    {
      id: '14',
      label: 'Bill Detail Report',
      path: '/reports/bill-detail',
    },
    {
      id: '15',
      label: 'Bed Occupancy Date View',
      path: '/reports/bed-occupancy',
    },
    {
      id: '16',
      label: 'Finance Dashboard',
      path: '/reports/finance-dashboard',
    },
  ];

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: 'flex' }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: '100%', 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Heading */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              my: '20px',
              color: '#5D6A82',
            }}
          >
            <Typography sx={{ 
              color: "#5D6A82", 
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "22px",
              lineHeight: "29px",
              letterSpacing: "0px"
            }}>Reports</Typography>
          </Box>
          {/* Search */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              my: '20px',
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder="Search by Name"
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ mb: '20px', color: '#5D6A82' }}>
              Categories
            </Typography>
            <Grid container spacing={3}>
              {REPORT_ITEMS?.filter((option) => {
                if (!search) return true;
                const searchLower = search.toLowerCase();
                if (searchBy === "name") {
                  return option?.label?.toLowerCase().includes(searchLower);
                }
                return true;
              }).map((option, index) => (
                <Grid item xs={12} sm={6} md={4} key={option?.id || index}>
                  <Link to={option?.path} style={{ textDecoration: 'none' }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      sx={{
                        color: 'black',
                        textTransform: 'none',
                        whiteSpace: 'normal',
                        height: 'auto',
                        minHeight: '56px',
                        padding: '8px 16px',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                    >
                      {option?.label || ''}
                    </Button>
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Reports;
